import { yupResolver } from "@hookform/resolvers/yup";
import { startCase, upperFirst } from "lodash";
import { FC, useContext, useMemo } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  dropdownAtomFamily,
  innerItemEditStyles,
  MAIN_DIRECTIONS_SELECT_ITEMS,
  TIME_END_DAY_HHmm,
  TNeighborAreaSettingsState,
  TNewNeighborAreaSettings,
  TNewNeighborAreaSettingsForm,
  ZERO_TIME_HHmm,
} from "@ryde/shared";

import {
  ColorIconButton,
  DropdownInputByControl,
  MultiSelectBoundInputByControl,
  NumberInputByControl,
  TimeInputByControl,
} from "../../../controls";
import { FullWidthTextFieldWrapper } from "../../parts-styled";
import { useNeighborAreasValidation } from "./hooks";
import { NeighborAreasTemplateContext } from "./neighbor-areas-template-context";

interface INeighborAreasSettingsItemEditProps {
  neighborAreaDetails?: TNeighborAreaSettingsState;
  onCancelEdit: () => void;
  neighborAreaDetailsIndex?: number;
}

export const NeighborAreasSettingsItemEdit: FC<
  INeighborAreasSettingsItemEditProps
> = ({ neighborAreaDetails, onCancelEdit }) => {
  const { fieldName, onEdit, onAdd, availableAreas } = useContext(
    NeighborAreasTemplateContext,
  );
  const { t } = useTranslation();
  const areaDropDownItems = useRecoilValue(dropdownAtomFamily("area"));
  const { neighborFormValidationSchema } = useNeighborAreasValidation();
  const availableAreaDropDownItems = useMemo(() => {
    const availableAreasSet = new Set(availableAreas);
    return areaDropDownItems.filter(item =>
      availableAreasSet.has(Number(item.value)),
    );
  }, [areaDropDownItems, availableAreas]);

  const {
    control,
    getValues,
    formState: { isValid },
  } = useForm<TNewNeighborAreaSettingsForm>({
    defaultValues: {
      ...neighborAreaDetails,
      fromTime: neighborAreaDetails?.fromTime || ZERO_TIME_HHmm,
      toTime: neighborAreaDetails?.toTime || TIME_END_DAY_HHmm,
    },
    mode: "all",
    resolver: yupResolver(neighborFormValidationSchema) as Resolver<
      TNewNeighborAreaSettingsForm,
      unknown
    >,
  });

  const onSaveClick = (): void => {
    if (!fieldName) {
      return;
    }

    const neighborAreaDetailsFormData = getValues();
    const fromTime = neighborAreaDetailsFormData.fromTime || ZERO_TIME_HHmm;
    const toTime = neighborAreaDetailsFormData.toTime || TIME_END_DAY_HHmm;

    const neighborAreaSettings: TNewNeighborAreaSettings = {
      ...neighborAreaDetailsFormData,
      fromTime,
      toTime,
    };

    if (neighborAreaDetails) {
      onEdit({
        tmpId: neighborAreaDetails?.tmpId,
        neighborAreaSettings:
          neighborAreaSettings as TNeighborAreaSettingsState,
      });
    } else {
      onAdd(neighborAreaSettings);
    }

    onCancelEdit();
  };

  const onCancelClick = (): void => onCancelEdit();

  return (
    <div
      className="NeighborAreasSettings-ItemEdit no-blur-item"
      style={innerItemEditStyles}
    >
      <DropdownInputByControl
        className="NeighborAreasSettings-ItemEditArea"
        name="areaId"
        control={control}
        formLabel={upperFirst(t("area.single"))}
        formValidation={{
          type: "number",
          rules: [
            {
              validation: "required",
            },
          ],
        }}
        formAttributes={{
          selectItems: availableAreaDropDownItems,
        }}
      />
      <DropdownInputByControl
        className="NeighborAreasSettings-ItemEditDirection"
        name="direction"
        control={control}
        formLabel={t("general.DIRECTION")}
        formValidation={{
          type: "number",
          rules: [],
        }}
        formAttributes={{
          selectItems: MAIN_DIRECTIONS_SELECT_ITEMS,
        }}
      />
      <TimeInputByControl
        className="NeighborAreasSettings-ItemEditFromTime"
        name="fromTime"
        control={control}
        fallbackTimeHHmm={neighborAreaDetails?.fromTime || ZERO_TIME_HHmm}
        formLabel={t("general.FROM")}
        formValidation={{
          type: "string",
          rules: [],
        }}
      />
      <TimeInputByControl
        className="NeighborAreasSettings-ItemEditToTime"
        name="toTime"
        control={control}
        fallbackTimeHHmm={neighborAreaDetails?.toTime || TIME_END_DAY_HHmm}
        formLabel={t("general.TO")}
        formValidation={{
          type: "string",
          rules: [],
        }}
      />
      <MultiSelectBoundInputByControl
        className="NeighborAreasSettings-ItemEditDayGroups"
        name="dayGroupIds"
        control={control}
        formLabel={startCase(t("dayGroup.plural"))}
        formAttributes={{
          dropItemsSource: "dayGroup",
        }}
      />
      <MultiSelectBoundInputByControl
        className="NeighborAreasSettings-ItemEditDayTypes"
        name="dayTypeIds"
        control={control}
        formLabel={startCase(t("dayType.plural"))}
        formAttributes={{
          dropItemsSource: "dayType",
        }}
      />
      <FullWidthTextFieldWrapper className="NeighborAreasSettings-ItemEditMaxSeats">
        <NumberInputByControl
          name="maxAreaSeats"
          formLabel={startCase(t("area.formLabel.maxAreaSeats"))}
          control={control}
        ></NumberInputByControl>
      </FullWidthTextFieldWrapper>
      <MultiSelectBoundInputByControl
        className="NeighborAreasSettings-ItemEditSites"
        name="siteIds"
        control={control}
        formLabel={startCase(t("site.plural"))}
        formAttributes={{
          dropItemsSource: "site",
        }}
      />

      <div className="NeighborAreasSettings-ItemEditButtons">
        <ColorIconButton
          iconType="CHECK"
          disabled={!isValid}
          onClick={onSaveClick}
        />

        <ColorIconButton iconType="CANCEL" onClick={onCancelClick} />
      </div>
    </div>
  );
};
